import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { api } from "@/lib/api";
import { LANGS, useI18n } from "@/lib/i18n";
import MobileMenu from "./MobileMenu";

/**
 * DANAmark primary navigation.
 *
 * - Fixed header; renders items from `site_settings.header_nav` (CMS driven)
 *   with a safe fallback so first paint is never empty.
 * - Dropdowns open on CLICK (keyboard + touch supported). Single top-level
 *   outside-click handler ensures clicking a different trigger switches in
 *   ONE click (no close-then-click race).
 * - Active state follows parent + child routes.
 */

/* ----- Default header_nav (used until the CMS payload arrives) -------- */
const DEFAULT_HEADER_NAV = [
  { id: "story", type: "link", url: "/mission", order: 10, show: true, emphasis: "default",
    label: { en: "Our Story", da: "Vores historie", es: "Nuestra historia" } },
  { id: "events", type: "dropdown", url: "/events", order: 20, show: true, emphasis: "default",
    label: { en: "Events", da: "Events", es: "Eventos" },
    children: [
      { id: "events_overview", url: "/events", order: 10, show: true,
        label: { en: "Events overview", da: "Oversigt over events", es: "Todos los eventos" } },
      { id: "events_ga", url: "/events/general-assembly-raffle-draw-2026", order: 20, show: false,
        label: { en: "Community Meeting & Live Raffle Draw", da: "Fællesskabsmøde og live lodtrækning", es: "Encuentro comunitario y sorteo en directo" } },
      { id: "events_social_run", url: "/events/social-run-for-spain", order: 30, show: true,
        label: { en: "Social Run for Spain 2026", da: "Social Run for Spain 2026", es: "Social Run for Spain 2026" } },
    ] },
  { id: "impact", type: "link", url: "/impact", order: 30, show: true, emphasis: "default",
    label: { en: "Impact", da: "Aftryk", es: "Impacto" } },
  { id: "involved", type: "dropdown", url: "/get-involved", order: 40, show: true, emphasis: "default",
    label: { en: "Get Involved", da: "Bliv en del af os", es: "Únete" },
    children: [
      { id: "involved_overview", url: "/get-involved", order: 10, show: true,
        label: { en: "Get Involved overview", da: "Oversigt — Bliv involveret", es: "Cómo colaborar" } },
      { id: "involved_join", url: "/join", order: 20, show: true,
        label: { en: "Become a member", da: "Bliv medlem", es: "Hazte miembro" } },
      { id: "involved_volunteer", url: "/volunteer", order: 30, show: true,
        label: { en: "Volunteer", da: "Bliv frivillig", es: "Sé voluntario" } },
      { id: "involved_partnership", url: "/partnership", order: 40, show: true,
        label: { en: "Partner with us", da: "Bliv partner", es: "Colabora con nosotros" } },
    ] },
  { id: "raffle", type: "link", url: "/raffle", order: 50, show: true, emphasis: "raffle",
    label: { en: "T-shirts", da: "T-shirts", es: "Camisetas" } },
  { id: "join", type: "link", url: "/join", order: 60, show: true, emphasis: "primary",
    label: { en: "Join", da: "Bliv medlem", es: "Hazte miembro" } },
];

export default function Header() {
  const { lang, setLang } = useI18n();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null); // id of open dropdown
  const [headerNav, setHeaderNav] = useState(null);
  const [brand, setBrand] = useState(null);
  const navRef = useRef(null);
  const triggersRef = useRef({}); // id → button element (focus return)

  // Close any open menu when the route changes.
  useEffect(() => { setMobileOpen(false); setOpenDrop(null); }, [loc.pathname, loc.hash]);

  useEffect(() => {
    api.get("/site-settings").then(r => {
      setHeaderNav(r.data?.header_nav || null);
      setBrand(r.data?.brand_assets || null);
    }).catch(() => { setHeaderNav(null); setBrand(null); });
  }, []);

  // Resolve CMS → fallback. Sort by order, hide show=false.
  const items = useMemo(() => {
    const src = Array.isArray(headerNav) && headerNav.length ? headerNav : DEFAULT_HEADER_NAV;
    return [...src]
      .filter(i => i && i.show !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(i => ({ ...i, children: (i.children || []).filter(c => c.show !== false).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) }));
  }, [headerNav]);

  const T = useCallback((obj) => (obj && (obj[lang] || obj.en || obj.da)) || "", [lang]);

  // Single outside-click handler at the nav level. If the click lands
  // outside the whole <nav>, close any open dropdown. If it lands inside
  // but not on the currently-open trigger, the trigger's onClick already
  // handles the switch — no close+open race.
  useEffect(() => {
    if (!openDrop) return;
    const onDocMouseDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenDrop(null);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpenDrop(prev => {
          if (prev && triggersRef.current[prev]) triggersRef.current[prev].focus();
          return null;
        });
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [openDrop]);

  const switchTo = useCallback((id) => {
    // Rock-solid switch: direct set, so clicking a different trigger with
    // another dropdown already open opens the new one in ONE click.
    setOpenDrop(prev => (prev === id ? null : id));
  }, []);

  // Active route helper — top-level matches overview + its children routes.
  const isItemActive = useCallback((item) => {
    const path = loc.pathname + loc.hash;
    const match = (url) => {
      if (!url) return false;
      const base = url.split("#")[0] || "/";
      if (loc.pathname === base) return true;
      if (base !== "/" && loc.pathname.startsWith(base + "/")) return true;
      if (url.includes("#") && path === url) return true;
      return false;
    };
    if (match(item.url)) return true;
    if (item.children) return item.children.some(c => match(c.url));
    return false;
  }, [loc.pathname, loc.hash]);

  return (
    <>
      <header data-testid="site-header"
              className="fixed top-0 left-0 right-0 z-50 bg-cream/95 backdrop-blur-md border-b border-line/70">
        <div className="max-editorial container-x flex items-center justify-between h-[68px] md:h-[76px] gap-4">
          {/* Brand */}
          <Link to="/" data-testid="brand-link" className="shrink-0 flex items-center" aria-label="DANAmark home">
            <img src={brand?.header_logo || "/images/logo-full.webp"}
                 alt={(brand?.logo_alt && (brand.logo_alt[lang] || brand.logo_alt.en)) || "DANAmark"}
                 data-testid="brand-logo"
                 className="hidden md:block h-11 lg:h-12 w-auto select-none" />
            <img src={brand?.header_logo || "/images/logo-symbol.webp"}
                 alt={(brand?.logo_alt && (brand.logo_alt[lang] || brand.logo_alt.en)) || "DANAmark"}
                 data-testid="brand-logo-symbol"
                 className="md:hidden h-10 w-auto select-none" />
          </Link>

          {/* Desktop nav */}
          <nav ref={navRef} className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Primary">
            {items.map(it => {
              if (it.type === "dropdown" && it.children?.length) {
                return (
                  <NavDropdown key={it.id}
                               id={it.id}
                               label={T(it.label)}
                               open={openDrop === it.id}
                               onToggle={() => switchTo(it.id)}
                               onClose={() => setOpenDrop(null)}
                               active={isItemActive(it)}
                               registerTrigger={(el) => { triggersRef.current[it.id] = el; }}>
                    {it.children.map(c => (
                      <DropdownLink key={c.id}
                                    to={c.url}
                                    onNavigate={() => setOpenDrop(null)}
                                    testid={`drop-${it.id}-${c.id}`}>
                        {T(c.label)}
                      </DropdownLink>
                    ))}
                  </NavDropdown>
                );
              }
              if (it.emphasis === "raffle") {
                return <RaffleLink key={it.id} to={it.url} label={T(it.label)} active={isItemActive(it)} />;
              }
              if (it.emphasis === "primary") {
                return <JoinPrimary key={it.id} to={it.url} label={T(it.label)} />;
              }
              return (
                <NavLink key={it.id} to={it.url} testid={`nav-${it.id}`} active={isItemActive(it)}>
                  {T(it.label)}
                </NavLink>
              );
            })}
          </nav>

          {/* Right side (mobile-only lang + menu button; primary CTA already in nav above) */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <LangSelector lang={lang} setLang={setLang} />
            <button data-testid="mobile-menu-btn"
                    onClick={() => setMobileOpen(v => !v)}
                    className="lg:hidden text-brand-dark w-10 h-10 flex items-center justify-center -mr-2"
                    aria-label={mobileOpen ? "Close menu" : "Open menu"}
                    aria-expanded={mobileOpen}>
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} items={items} />
    </>
  );
}

/* ----- Primary link -------------------------------------------------- */
function NavLink({ to, testid, active, children }) {
  return (
    <Link to={to} data-testid={testid}
          aria-current={active ? "page" : undefined}
          className={`text-[14px] font-semibold tracking-wide transition-colors ${active ? "text-brand-coral" : "text-brand-dark/85 hover:text-brand-coral"}`}>
      {children}
    </Link>
  );
}

/* ----- Dropdown trigger + panel (CLICK-only, keyboard a11y) ---------- */
function NavDropdown({ id, label, open, onToggle, onClose, active, registerTrigger, children }) {
  const panelId = `dropdown-${id}`;
  const btnRef = useRef(null);
  useEffect(() => { if (registerTrigger && btnRef.current) registerTrigger(btnRef.current); }, [registerTrigger]);

  // On open, focus first menu item for keyboard users.
  const panelRef = useRef(null);
  useEffect(() => {
    if (open && panelRef.current) {
      const first = panelRef.current.querySelector("a,button");
      if (first) first.focus();
    }
  }, [open]);

  const onBtnKey = (e) => {
    if (e.key === "ArrowDown" && !open) { e.preventDefault(); onToggle(); }
  };

  return (
    <div className="relative">
      <button ref={btnRef}
              data-testid={`nav-${id}`}
              type="button"
              onClick={onToggle}
              onKeyDown={onBtnKey}
              aria-expanded={open}
              aria-haspopup="menu"
              aria-controls={panelId}
              className={`inline-flex items-center gap-1 text-[14px] font-semibold tracking-wide transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-coral focus-visible:outline-offset-4 ${active ? "text-brand-coral" : "text-brand-dark/85 hover:text-brand-coral"}`}>
        {label}
        <ChevronDown size={14} strokeWidth={2}
                     className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div id={panelId}
             ref={panelRef}
             role="menu"
             aria-labelledby={`nav-${id}`}
             data-testid={`nav-${id}-panel`}
             className="absolute top-full left-0 mt-2 min-w-[280px] max-w-[90vw] bg-white border border-line/70 rounded-md shadow-xl py-2 z-50">
          {children}
        </div>
      )}
    </div>
  );
}

function DropdownLink({ to, children, onNavigate, testid }) {
  return (
    <Link to={to}
          role="menuitem"
          data-testid={testid}
          onClick={onNavigate}
          className="block px-4 py-2.5 text-[14px] text-brand-dark hover:bg-brand-sand hover:text-brand-coral focus-visible:bg-brand-sand focus-visible:text-brand-coral focus-visible:outline-none transition-colors">
      {children}
    </Link>
  );
}

/* ----- Highlighted controls ------------------------------------------ */
function RaffleLink({ to, label, active }) {
  return (
    <Link to={to} data-testid="nav-raffle"
          aria-current={active ? "page" : undefined}
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 transition-colors duration-200 text-[13px] font-semibold tracking-wide ${active ? "bg-brand-coral text-white border border-brand-coral" : "border border-brand-coral text-brand-coral hover:bg-brand-coral hover:text-white"}`}>
      {label.toUpperCase()}
    </Link>
  );
}

function JoinPrimary({ to, label }) {
  return (
    <Link to={to} data-testid="nav-join"
          className="hidden md:inline-flex items-center px-5 py-2.5 bg-brand-dark text-white hover:bg-brand-coral transition-colors duration-200 text-sm font-semibold tracking-wide rounded-sm">
      {label}
    </Link>
  );
}

function LangSelector({ lang, setLang }) {
  // Language selector persists via useI18n — handled by i18n provider.
  return (
    <div className="hidden sm:flex items-center gap-1 text-[12px] font-semibold" data-testid="lang-selector">
      {LANGS.map((l, i) => (
        <React.Fragment key={l.code}>
          <button onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}
                  aria-current={lang === l.code ? "true" : undefined}
                  className={`transition-colors px-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-coral focus-visible:outline-offset-2 ${lang === l.code ? "text-brand-coral" : "text-brand-dark/50 hover:text-brand-dark"}`}>
            {l.label}
          </button>
          {i < LANGS.length - 1 && <span className="text-brand-dark/25">·</span>}
        </React.Fragment>
      ))}
    </div>
  );
}
